import React, { useState, useEffect, useMemo } from 'react';
import styled, { createGlobalStyle } from 'styled-components';
import EventCard from './EventCard';
import { fetchEvents } from '../utils/api';
import { getStylesFromUrl } from '../utils/styleConfig';

const Container = styled.div`
  background: ${props => props.styles.backgroundColor || '#ffffff'};
  font-family: ${props => {
    console.log('Font being applied:', props.styles.fontFamily); // Debug log
    return props.styles.fontFamily || 'system-ui';
  }};
  width: 100%;
  overflow: hidden;
`;

const Grid = styled.div`
  display: grid;
  grid-template-columns: repeat(${props =>
    Math.min(props.cardsPerRow || 4, 4)}, minmax(${props => props.minCardWidth || '250px'}, 1fr));
  gap: ${props => props.gap || '16px'};
  font-family: ${props => props.styles.fontFamily || 'system-ui'}; // Added font-family
  
  @media (max-width: 1200px) {
    grid-template-columns: repeat(${props =>
    Math.min(props.cardsPerRow || 3, 3)}, minmax(${props => props.minCardWidth || '250px'}, 1fr));
  }
  
  @media (max-width: 900px) {
    grid-template-columns: repeat(${props =>
    Math.min(props.cardsPerRow || 2, 2)}, minmax(${props => props.minCardWidth || '250px'}, 1fr));
  }
  
  @media (max-width: 600px) {
    grid-template-columns: 1fr;
  }
`;

function findNextUpcomingDateIndex(dates) {
  const now = new Date();
  now.setHours(0, 0, 0, 0);

  const futureDates = dates
    .map((dateStr, index) => ({
      date: new Date(dateStr.split('T')[0]),
      index
    }))
    .filter(({ date }) => date >= now);

  if (futureDates.length === 0) {
    return dates.length - 1;
  }

  futureDates.sort((a, b) => a.date.getTime() - b.date.getTime());
  return futureDates[0].index;
}

const EventsList = () => {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const styles = useMemo(() => getStylesFromUrl(), []);

  const params = new URLSearchParams(window.location.search);
  const organization = params.get('organization');
  const cardsPerRow = parseInt(params.get('cardsPerRow') || '4', 10);
  const cardWidth = params.get('cardWidth') || '250px';
  const cardGap = params.get('cardGap') || '16px';

  useEffect(() => {
    const loadEvents = async () => {
      try {
        setLoading(true);
        const eventData = await fetchEvents(organization);
        setEvents(eventData);
      } catch (err) {
        setError('Failed to load events');
        console.error('Error loading events:', err);
      } finally {
        setLoading(false);
      }
    };

    if (organization) {
      loadEvents();
    } else {
      setError('Organization parameter is required');
      setLoading(false);
    }
  }, [organization]);

  // send component height
  useEffect(() => {
    const sendHeight = () => {
      console.log('Sending height:', document.documentElement.scrollHeight);
      window.parent.postMessage({
        type: 'resize',
        height: document.documentElement.scrollHeight
      }, '*');
    };

    if (!loading && events.length > 0) {
      setTimeout(sendHeight, 100);
    }

    const resizeObserver = new ResizeObserver(() => {
      sendHeight();
    });

    resizeObserver.observe(document.documentElement);

    return () => resizeObserver.disconnect();
  }, [loading, events]);

  
  if (loading) {
    return <div>Loading events...</div>;
  }

  if (error) {
    return <div>Error: {error}</div>;
  }

  return (
    <Container styles={styles}>
      <Grid
        cardsPerRow={cardsPerRow}
        minCardWidth={cardWidth}
        gap={cardGap}
        styles={styles}
      >
        {events.map((event, index) => {
          const dateIndex = event.frequency === "Once" ? 0 : findNextUpcomingDateIndex(event.dates);
          const constructedDate = `${event.dates[dateIndex].split('T')[0]}T${event.start_time}`;

          return (
            <EventCard
              key={index}
              title={event.name}
              date={constructedDate}
              image={event.img}
              location={event.location}
              href={`https://ummahfy.com/event/${event.event_id}`}
              styles={styles}
            />
          );
        })}
      </Grid>
    </Container>
  );
};

export default EventsList;